import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useSystemTemplateTranslations } from '../../../queries/Templates';
import { Label } from '../../components-v2/Label';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../components-v2/Select';
import { LoadingSpinner } from '../LoadingSpinner';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	disabled?: boolean;
	onTranslatedTemplateSelected?(template: Api.ITemplate): void;
	originalTemplate: Api.ITemplate;
	selectedTemplate?: Api.ITemplate;
}

interface ILanguageOption {
	title: string;
	value: string;
}

const ORIGINAL_CONTENT_VALUE = 'original';

export function SystemTemplateTranslationSelector({
	disabled,
	onTranslatedTemplateSelected,
	originalTemplate,
	selectedTemplate,
}: IProps) {
	const labelId = React.useId();
	const {
		data: translatedTemplates,
		isError: hasError,
		isLoading,
	} = useSystemTemplateTranslations({
		id: originalTemplate?.id,
		meta: {
			errorLogging: {
				category: 'SystemTemplateTranslationSelector',
				action: 'SystemTemplateTranslationsLoad-Error',
			},
		},
	});
	const options = React.useMemo(() => {
		if (translatedTemplates?.length) {
			const result = translatedTemplates.map<ILanguageOption>(t => ({
				title: t.languageDescription ?? t.language,
				value: t.language,
			}));
			if (originalTemplate) {
				return [{ title: 'Original Content', value: ORIGINAL_CONTENT_VALUE }].concat(result);
			}
			return result;
		}
		return [];
	}, [translatedTemplates, originalTemplate]);

	const onValueChange = (value: string) => {
		onTranslatedTemplateSelected?.(
			value === ORIGINAL_CONTENT_VALUE ? originalTemplate : translatedTemplates.find(x => x.language === value)
		);
	};

	if (hasError || (!isLoading && !options?.length)) {
		return null;
	}

	const selectedOption = options.find(x => selectedTemplate?.language === x.value);

	return (
		<div className={css(styleSheet.container)}>
			<Label id={labelId} styles={[styleSheet.label]}>
				Translate to:
			</Label>
			<Select value={selectedOption?.value ?? null} disabled={isLoading || disabled} onValueChange={onValueChange}>
				<SelectTrigger aria-labelledby={labelId} styles={[styleSheet.trigger]}>
					<span className={css(styleSheet.triggerValue)}>
						{isLoading ? <LoadingSpinner type='tiny' /> : null}
						{selectedOption ? (
							<span>{selectedOption.title}</span>
						) : (
							<span className={css(styleSheet.placeholder)}>Choose Language</span>
						)}
					</span>
				</SelectTrigger>
				<SelectContent>
					{options.map(option => (
						<SelectItem key={option.value} value={option.value}>
							{option.title}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
}
